/**
 * Interval Song References
 *
 * Famous song examples to help users recognize intervals.
 * Each interval includes a memorable song reference and a brief description
 * of its emotional character.
 */

export interface IntervalReference {
    name: string;
    semitones: number;
    songExample: string;
    songHint: string;  // Short hint like "first 2 notes"
    character: string; // Emotional quality
}

export const intervalReferences: Record<string, IntervalReference> = {
    "Unison": {
        name: "Unison",
        semitones: 0,
        songExample: "Same note repeated",
        songHint: "no movement",
        character: "Static, same pitch"
    },
    "Minor 2nd": {
        name: "Minor 2nd",
        semitones: 1,
        songExample: "Jaws theme",
        songHint: "dun-DUN",
        character: "Tense, creepy"
    },
    "Major 2nd": {
        name: "Major 2nd",
        semitones: 2,
        songExample: "Happy Birthday",
        songHint: "Hap-py",
        character: "Stepwise, neutral"
    },
    "Minor 3rd": {
        name: "Minor 3rd",
        semitones: 3,
        songExample: "Greensleeves",
        songHint: "A-las",
        character: "Sad, melancholic"
    },
    "Major 3rd": {
        name: "Major 3rd",
        semitones: 4,
        songExample: "When the Saints",
        songHint: "Oh when",
        character: "Happy, bright"
    },
    "Perfect 4th": {
        name: "Perfect 4th",
        semitones: 5,
        songExample: "Here Comes the Bride",
        songHint: "Here comes",
        character: "Strong, anthem-like"
    },
    "Tritone": {
        name: "Tritone",
        semitones: 6,
        songExample: "The Simpsons",
        songHint: "The Simp-",
        character: "Unstable, jazzy"
    },
    "Perfect 5th": {
        name: "Perfect 5th",
        semitones: 7,
        songExample: "Star Wars",
        songHint: "Star Wars",
        character: "Powerful, open"
    },
    "Minor 6th": {
        name: "Minor 6th",
        semitones: 8,
        songExample: "The Entertainer",
        songHint: "opening leap",
        character: "Yearning, plaintive"
    },
    "Major 6th": {
        name: "Major 6th",
        semitones: 9,
        songExample: "My Bonnie",
        songHint: "My Bon-nie",
        character: "Warm, romantic"
    },
    "Minor 7th": {
        name: "Minor 7th",
        semitones: 10,
        songExample: "Somewhere (West Side Story)",
        songHint: "There's a",
        character: "Bluesy, soulful"
    },
    "Major 7th": {
        name: "Major 7th",
        semitones: 11,
        songExample: "Take On Me",
        songHint: "Take on",
        character: "Dreamy, dissonant"
    },
    "Octave": {
        name: "Octave",
        semitones: 12,
        songExample: "Somewhere Over the Rainbow",
        songHint: "Some-where",
        character: "Complete, same note higher"
    }
};

// Helper to get reference by interval name
export const getIntervalReference = (name: string): IntervalReference | undefined => {
    return intervalReferences[name];
};

// Helper to get all intervals as array (useful for iteration)
export const getAllIntervalReferences = (): IntervalReference[] => {
    return Object.values(intervalReferences);
};
